"use client";

import Image from "next/image";
import { useLocale } from "@/lib/locale/locale-provider";
import type { StorySectionProps } from "@/lib/sections/story.types";

export function WeddingStorySection({ title, intro, blocks }: StorySectionProps) {
  const locale = useLocale();
  const closingLine =
    locale === "fr"
      ? "Et ce n'est que le début de notre belle histoire..."
      : locale === "en"
        ? "And this is only the beginning of our beautiful story..."
        : "Und das ist erst der Anfang unserer gemeinsamen Geschichte...";

  return (
    <section className="bg-[#FFF8F0] px-4 py-20">
      <div className="mx-auto max-w-6xl space-y-20">
        <div className="space-y-4 text-center">
          <h2 className="text-3xl font-serif">{title}</h2>

          {intro ? (
            <p className="mx-auto max-w-xl text-base text-gray-600">{intro}</p>
          ) : null}
        </div>

        <div className="space-y-20">
          {blocks.map((block, index) => {
            const reverse = index % 2 !== 0;

            return (
              <div
                key={`${block.title}-${index}`}
                className="grid grid-cols-1 items-center gap-6 md:grid-cols-2"
              >
                {block.image ? (
                  <div className={`flex justify-center ${reverse ? "md:order-2" : "md:order-1"}`}>
                    <div className="relative h-40 w-40 overflow-hidden rounded-full shadow-lg sm:h-52 sm:w-52">
                      <Image
                        src={block.image}
                        alt={block.title}
                        fill
                        sizes="(max-width: 639px) 160px, 208px"
                        className="object-cover"
                      />
                    </div>
                  </div>
                ) : null}

                <div
                  className={`space-y-3 ${
                    reverse ? "text-left md:order-1 md:text-right" : "text-left md:order-2"
                  }`}
                >
                  <h3 className="text-lg font-serif sm:text-xl">{block.title}</h3>

                  <p className="text-sm leading-relaxed text-gray-700 sm:text-base">
                    {block.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <p className="pt-12 text-center font-script text-xl text-amber-600">
          {closingLine}
        </p>
      </div>
    </section>
  );
}
